import React, { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { SubscriptionProvider } from '../contexts/SubscriptionContext';
import WhistleBlowerBubble from '../components/common/WhistleBlowerBubble';
import UmaChatLauncher from '../components/uma/UmaChatLauncher';
import AiStrip from '../components/ai/AiStrip';

// Plan 05b-02 — RoleLayout is a thin role-dispatcher; sub-routes live inside
// each role layout. Role 3 (Audit / staff portal) sub-routes are registered
// in ../pages/AuditorPage and include:
//   /auditor/procedure-instance/:id  ->  ProcedureInstancePage
// (see also ../views/auditor/ProcedureInstancePage.tsx).

type LayoutModule = { default: React.ComponentType<any> };

const ROLE_LAYOUT: Record<number, () => Promise<LayoutModule>> = {
  1: () => import('./application1/Layout1'),  // GRC Team
  2: () => import('./application3/Layout3'),  // Staff / Org Member
  3: () => import('./application5/Layout5'),  // Audit — Phase 106 plan 05 (R11/D-10)
  4: () => import('./application4/Layout4'),  // Risk Analyst
  5: () => import('./application2/Layout2'),  // Executive / C-Suite
  6: () => import('./application6/Layout6'),  // Costing
};

// Cache: avoid creating a new React.lazy on every render
const cache = new Map<number, React.LazyExoticComponent<React.ComponentType<any>>>();

const RoleLayout: React.FC = () => {
  const { state } = useAuth();
  const { setRole } = useTheme();
  const roleId = state.user?.role ? Number(state.user.role) : null;

  useEffect(() => {
    setRole(roleId);
  }, [roleId, setRole]);

  const Layout = useMemo(() => {
    if (!roleId || !ROLE_LAYOUT[roleId]) return null;
    if (!cache.has(roleId)) {
      cache.set(roleId, React.lazy(ROLE_LAYOUT[roleId]));
    }
    return cache.get(roleId)!;
  }, [roleId]);

  if (!Layout) return <Navigate to="/login" replace />;

  return (
    <SubscriptionProvider>
      <React.Suspense fallback={<div className="app-layout-loading" />}>
        <Layout />
        <WhistleBlowerBubble />
        <UmaChatLauncher />
        <AiStrip />
      </React.Suspense>
    </SubscriptionProvider>
  );
};

export default RoleLayout;
